
import { useState, useCallback } from 'react';
import { GamePlanner2Header } from './GamePlanner2Header';
import { GamePlanningTips } from './GamePlanningTips';
import { GamePlanner2Editor } from './GamePlanner2Editor';
import { PlanListSection } from './components/PlanListSection';
import { PlanDialogs } from './components/PlanDialogs';
import { ShareGamePlan2Dialog } from './components/ShareGamePlan2Dialog';
import { useGamePlanOperations } from './hooks/useGamePlanOperations';
import { usePlansLoader } from './hooks/usePlansLoader';
import { usePlanSorting } from './hooks/usePlanSorting';
import { GamePlan } from '@/types/gamePlanner';
import { OfflineWrapper, OfflineDataStatus } from '@/contexts/offline/components/OfflineUIComponents';

export function GamePlanner2View() {
  // State for the view
  const [currentView, setCurrentView] = useState<'list' | 'editor'>('list');
  const [activeTab, setActiveTab] = useState<'templates' | 'myPlans'>('templates');
  const [selectedPlan, setSelectedPlan] = useState<GamePlan | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [planToShare, setPlanToShare] = useState<GamePlan | null>(null);
  
  // Load game plans and templates
  const {
    gamePlans,
    setGamePlans,
    templates,
    setTemplates,
    loading,
    loadGamePlans,
    loadTemplates
  } = usePlansLoader();

  // Sorting and searching
  const {
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    getSortedPlans
  } = usePlanSorting();

  // Get sorted plans based on active tab
  const sortedPlans = getSortedPlans(activeTab === 'myPlans' ? gamePlans : templates);

  // Game plan operations
  const {
    // Dialog states
    deleteDialogOpen,
    setDeleteDialogOpen,
    detailsDialogOpen,
    setDetailsDialogOpen,
    confirmTemplateDialogOpen,
    setConfirmTemplateDialogOpen,
    
    // Editor form states
    editedName,
    setEditedName,
    editedDescription,
    setEditedDescription,
    
    // Operation handlers
    handleDeletePlan,
    confirmDeletePlan,
    
    handleConvertToTemplate,
    confirmConvertToTemplate,
    
    handleChangeDetails,
    handleSaveDetails
  } = useGamePlanOperations({
    gamePlans,
    setGamePlans,
    templates,
    setTemplates,
    loadGamePlans,
    loadTemplates,
  });
  
  // Function to go back to list view
  const handleCancelEdit = useCallback(() => {
    setCurrentView('list');
    setSelectedPlan(null);
  }, []);
  
  // Handle editor save completion
  const handleSaveComplete = useCallback(() => {
    setCurrentView('list');
    setSelectedPlan(null);
    
    // Reload game plans and templates
    loadGamePlans();
    loadTemplates();
  }, [loadGamePlans, loadTemplates]);
  
  // Function to create a new plan
  const handleCreateNew = useCallback(() => {
    setSelectedPlan(null);
    setCurrentView('editor');
  }, []);

  // Handle edit plan
  const handleEdit = useCallback((id: string) => {
    const plan = [...gamePlans, ...templates].find(p => p.id === id);
    if (plan) {
      setSelectedPlan(plan);
      setCurrentView('editor');
    }
  }, [gamePlans, templates]);
  
  // Handle share plan
  const handleSharePlan = useCallback((id: string) => {
    const plan = [...gamePlans, ...templates].find(p => p.id === id);
    if (plan) {
      setPlanToShare(plan);
      setShareDialogOpen(true);
    }
  }, [gamePlans, templates]);
  
  return currentView === 'editor' ? (
    <GamePlanner2Editor
      onCancel={handleCancelEdit}
      onSaved={handleSaveComplete}
      currentPlan={selectedPlan}
    />
  ) : (
    <OfflineWrapper>
      <div className="container py-6 max-w-6xl">
        {/* Offline data status indicator */}
        <OfflineDataStatus className="mb-4" />

        {/* Header with create button */}
        <GamePlanner2Header onNewPlan={handleCreateNew} />

        {/* Tips and guidance */}
        <GamePlanningTips className="mb-6" />

        {/* Plan list section with tabs and sorting */}
        <PlanListSection
          activeTab={activeTab}
          onTabChange={setActiveTab}
          sortBy={sortBy}
          onSortChange={setSortBy}
          sortedPlans={sortedPlans}
          loading={loading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onEdit={handleEdit}
          onShare={handleSharePlan}
          onDelete={handleDeletePlan}
          onChangeDetails={handleChangeDetails}
          onConvertToTemplate={handleConvertToTemplate}
        />
      
      {/* Dialogs for delete, edit details, convert to template */}
      <PlanDialogs
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        handleConfirmDelete={confirmDeletePlan}
        
        detailsDialogOpen={detailsDialogOpen}
        setDetailsDialogOpen={setDetailsDialogOpen}
        editedName={editedName}
        editedDescription={editedDescription}
        onNameChange={setEditedName}
        onDescriptionChange={setEditedDescription}
        handleSaveDetailsConfirm={handleSaveDetails}
        
        confirmTemplateDialogOpen={confirmTemplateDialogOpen}
        setConfirmTemplateDialogOpen={setConfirmTemplateDialogOpen}
        handleConfirmConvertToTemplate={confirmConvertToTemplate}
      />
      
      {/* Share dialog */}
      <ShareGamePlan2Dialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        gamePlan={planToShare}
      />
      </div>
    </OfflineWrapper>
  );
}
